import type { AccessSourceType, PermissionDirective } from '@thinkalpha/common/contracts/permissions.js';

/** Allow-directive subject; `type` is only present on typed directives. */
export interface AllowDirectiveSubject {
    id: string;
    type?: AccessSourceType;
}

/**
 * Extract the subject of an allow directive, including the subject type when the directive
 * carries one (typed shape). Returns null for deny directives (both legacy and typed).
 */
export function getAllowDirectiveSubject(directive: PermissionDirective): AllowDirectiveSubject | null {
    if (typeof directive === 'string') return { id: directive };
    if ('mode' in directive) return directive.mode === 'allow' ? { id: directive.id, type: directive.type } : null;
    if ('deny' in directive) return null;
    return null;
}

/**
 * Extract the subject ID from any shape of permission directive.
 * Returns null for deny directives (both legacy and typed).
 */
export function getDirectiveSubjectId(directive: PermissionDirective): string | null {
    return getAllowDirectiveSubject(directive)?.id ?? null;
}

/**
 * Extract allow-only subject IDs from a permission directive array.
 */
export function getAllowSubjectIds(directives: PermissionDirective[]): string[] {
    const ids: string[] = [];
    for (const directive of directives) {
        const id = getDirectiveSubjectId(directive);
        if (id !== null) ids.push(id);
    }
    return ids;
}
